import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';

import type { WebsiteDraft, WebsiteStepProps } from '../types';
import { uploadMedia, publicURL as mediaPublicURL } from '../../api/media';
import { analyzeLogo } from '../../api/websites';
import { ApiError } from '../../api/client';
import { ImageCropDialog } from '../../components/ImageCropDialog';
import { LanguagesField } from '../../components/LanguagesField';

// Branding is step 2: upload the logo, pick the brand palette. The
// preview chip on the right updates live as the user types in the
// color pickers / hex inputs so they can see the impact before the
// site renders.
//
// Save discipline: this step writes the BRAND draft only. The actual
// PATCH to /general happens at the end (Preview step) so a half-finished
// wizard doesn't leave the org with a partial color update.

// 12-color brand swatch — quick picks that feel sport-club-appropriate.
// Users can still type any hex.
const SWATCHES = [
  '#BEE034', '#26272B', '#A6EFFF', '#FFA6EA', '#FEB000', '#FFFDD8',
  '#0F62FE', '#DA1E28', '#198038', '#8A3FFC', '#6E6E6E', '#FFFFFF',
];

function ColorField({
  label,
  value,
  onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="wizard-color-field">
      <div className="wizard-color-field__head">
        <label>{label}</label>
        <input
          type="text"
          className="wizard-color-field__hex"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#bee034"
          spellCheck={false}
          aria-label={`${label} hex`}
        />
        <input
          type="color"
          className="wizard-color-field__picker"
          value={isValidHex(value) ? value : '#bee034'}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`${label} picker`}
        />
      </div>
      <div className="wizard-color-field__swatches">
        {SWATCHES.map((s) => (
          <button
            key={s}
            type="button"
            className="wizard-color-field__swatch"
            style={{ background: s }}
            aria-label={s}
            aria-pressed={value.toLowerCase() === s.toLowerCase()}
            onClick={() => onChange(s)}
          />
        ))}
      </div>
    </div>
  );
}

function isValidHex(s: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(s);
}

export function Branding({ draft, update }: WebsiteStepProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const { slug = '' } = useParams<{ slug: string }>();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  // analyzingColors goes true while the pixel analyzer is inspecting
  // the uploaded logo. The Branding step shows a small "Picking
  // colors…" hint so the inputs filling in unprompted isn't a jump-scare.
  const [analyzingColors, setAnalyzingColors] = useState(false);
  // analyzeFailed surfaces a small hint when the analyzer returned an
  // error (most commonly: storage not configured in dev → 503).
  // The user can still pick colors manually; this is just feedback so
  // a click on the manual button doesn't appear to do nothing.
  const [analyzeFailed, setAnalyzeFailed] = useState(false);
  // Prevent the on-mount auto-analyze from running more than once per
  // session — without this, every render after the analyze itself calls
  // update() could re-fire (especially if React strict-mode double-runs effects).
  const autoTriedRef = useRef(false);

  // runAnalysis is shared by:
  //   - fresh-upload path in onLogoChosen (force=false)
  //   - on-mount auto-run when an existing logo loads (force=false)
  //   - manual "Analyze logo colors" button (force=true → overwrite)
  //
  // When force is false we never clobber a hex the admin already typed.
  // When force is true (explicit click) we replace existing values —
  // that's the intent of a manual retrigger.
  const runAnalysis = async (objectKey: string, force: boolean) => {
    setAnalyzingColors(true);
    setAnalyzeFailed(false);
    try {
      const token = auth.user?.access_token ?? null;
      const palette = await analyzeLogo(token, slug, objectKey);
      const patch: Partial<WebsiteDraft> = {};
      if ((force || !draft.primaryColor) && palette.primary) patch.primaryColor = palette.primary;
      if ((force || !draft.accentColor)  && palette.accent)  patch.accentColor  = palette.accent;
      if ((force || !draft.mutedColor)   && palette.muted)   patch.mutedColor   = palette.muted;
      if (Object.keys(patch).length > 0) {
        update(patch);
      } else if (force) {
        // Force-clicked but analyzer returned nothing usable — show
        // the failure hint so the click doesn't appear to do nothing.
        setAnalyzeFailed(true);
      }
    } catch {
      setAnalyzeFailed(true);
    } finally {
      setAnalyzingColors(false);
    }
  };

  // Auto-analyze on mount when:
  //   - a logo is already uploaded (from a previous wizard session OR
  //     the org's General settings page)
  //   - AND none of the three color fields are set yet
  //
  // This catches the "I opened the wizard, my logo is there, why
  // didn't AI pick the colors?" case. Skipped on re-mount via the
  // ref guard so it runs at most once per page load.
  useEffect(() => {
    if (autoTriedRef.current) return;
    if (!draft.logoObjectKey) return;
    if (draft.primaryColor || draft.accentColor || draft.mutedColor) return;
    autoTriedRef.current = true;
    void runAnalysis(draft.logoObjectKey, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogoChosen = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const token = auth.user?.access_token ?? null;
      const result = await uploadMedia(token, slug, file);
      update({ logoObjectKey: result.object_key });
      // Fresh upload — always analyze. Mark autoTried so the on-mount
      // effect doesn't try again on the next render.
      autoTriedRef.current = true;
      await runAnalysis(result.object_key, false);
    } catch (err) {
      if (err instanceof ApiError) {
        setUploadError(err.code);
      } else {
        setUploadError('upload_failed');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="wizard-branding">
      {/* Right-hand column (on wide screens): logo uploader with the live
          preview card stacked directly beneath it. Wrapped so their
          heights are independent of the taller colors/languages columns. */}
      <div className="wizard-branding__side">
        <section className="wizard-branding__logo">
        <h3>{t('website.wizard.branding.logoHeading')}</h3>
        {draft.logoObjectKey ? (
          <div className="wizard-branding__logo-current">
            <img src={mediaPublicURL(draft.logoObjectKey)} alt="" />
            <span className="wizard-branding__logo-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setCropOpen(true)}
              >
                {t('imageInput.crop')}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => update({ logoObjectKey: '' })}>
                {t('website.wizard.branding.removeLogo')}
              </button>
            </span>
            <ImageCropDialog
              open={cropOpen}
              src={mediaPublicURL(draft.logoObjectKey)}
              filename={draft.logoObjectKey}
              onCrop={async (file) => {
                const token = auth.user?.access_token ?? null;
                const result = await uploadMedia(token, slug, file);
                update({ logoObjectKey: result.object_key });
                // Re-run colour analysis on the cropped variant — the
                // dominant palette can shift meaningfully after a crop
                // that drops borders / backgrounds.
                void runAnalysis(result.object_key, false);
              }}
              onClose={() => setCropOpen(false)}
            />
          </div>
        ) : (
          <div className="wizard-branding__logo-empty">
            <p>{t('website.wizard.branding.logoEmpty')}</p>
          </div>
        )}
        <label className="btn btn--ghost wizard-branding__upload">
          {uploading ? t('common.submitting') : t('website.wizard.branding.upload')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onLogoChosen(f);
              e.target.value = '';
            }}
          />
        </label>
        {uploadError && (
          <p role="alert" className="error">
            {t(`website.wizard.branding.uploadError.${uploadError}`, {
              defaultValue: t('website.wizard.branding.uploadError.unknown'),
            })}
          </p>
        )}
        </section>

        <aside className="wizard-branding__preview" aria-label={t('website.wizard.branding.previewLabel')}>
          <div
            className="wizard-branding__preview-card"
            style={{
              background: isValidHex(draft.mutedColor) ? draft.mutedColor : '#FFFDD8',
            }}
          >
            <div
              className="wizard-branding__preview-bar"
              style={{
                background: isValidHex(draft.primaryColor) ? draft.primaryColor : '#BEE034',
                color: isValidHex(draft.accentColor) ? draft.accentColor : '#26272B',
              }}
            >
              {t('website.wizard.branding.previewSampleNav')}
            </div>
            <div className="wizard-branding__preview-body">
              <strong style={{ color: isValidHex(draft.accentColor) ? draft.accentColor : '#26272B' }}>
                {t('website.wizard.branding.previewSampleHeading')}
              </strong>
              <p>{t('website.wizard.branding.previewSampleBody')}</p>
            </div>
          </div>
        </aside>
      </div>

      <section className="wizard-branding__colors">
        <header className="wizard-branding__colors-head">
          <h3>{t('website.wizard.branding.colorsHeading')}</h3>
          {draft.logoObjectKey && (
            <button
              type="button"
              className="btn btn--ghost wizard-branding__analyze-btn"
              onClick={() => void runAnalysis(draft.logoObjectKey, true)}
              disabled={analyzingColors}
              title={t('website.wizard.branding.analyzeBtnHint', {
                defaultValue: 'Replace the current colors with picks from your logo',
              })}
            >
              {analyzingColors
                ? t('common.submitting')
                : t('website.wizard.branding.analyzeBtn', { defaultValue: 'Pick colors from logo' })}
            </button>
          )}
        </header>
        {analyzingColors && (
          <p className="wizard-branding__analyzing" role="status">
            {t('website.wizard.branding.analyzingColors', {
              defaultValue: 'Picking brand colors from your logo…',
            })}
          </p>
        )}
        {!analyzingColors && analyzeFailed && (
          <p className="wizard-branding__analyze-failed" role="status">
            {t('website.wizard.branding.analyzeFailed', {
              defaultValue: "Couldn't read brand colors from the logo automatically. Pick them below.",
            })}
          </p>
        )}
        <ColorField
          label={t('website.wizard.branding.primary')}
          value={draft.primaryColor}
          onChange={(v) => update({ primaryColor: v })}
        />
        <ColorField
          label={t('website.wizard.branding.accent')}
          value={draft.accentColor}
          onChange={(v) => update({ accentColor: v })}
        />
        <ColorField
          label={t('website.wizard.branding.muted')}
          value={draft.mutedColor}
          onChange={(v) => update({ mutedColor: v })}
        />
      </section>

      <section className="wizard-branding__languages">
        <LanguagesField
          supported={draft.supportedLanguages}
          primary={draft.primaryLanguage}
          onChange={({ supported, primary }) =>
            update({ supportedLanguages: supported, primaryLanguage: primary })
          }
        />
      </section>
    </div>
  );
}

export function brandingIsValid(d: WebsiteDraft): boolean {
  // Branding is "as much as you want" — empty colors fall back to the
  // design system, so this step is never blocking. Only reject malformed
  // hex strings (non-empty but not 7-char #rrggbb).
  for (const v of [d.primaryColor, d.accentColor, d.mutedColor]) {
    if (v !== '' && !isValidHex(v)) return false;
  }
  return true;
}
